import type { ReactNode } from 'react';
import { Modal } from './Modal';
import { Button } from './Button';

export type ConfirmDialogTone = 'primary' | 'destructive';

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: ConfirmDialogTone;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
  busy?: boolean;
}

/**
 * F-160 (C4): generic yes/no confirm built on top of `<Modal>`. Use this
 * before high-stakes / destructive actions so the user gets a moment to
 * reconsider (NN/g H5 error prevention).
 *
 * The dialog itself is intentionally stateless — caller owns the `open`
 * flag and the busy / async wiring around `onConfirm`.
 */
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel = 'Підтвердити',
  cancelLabel = 'Скасувати',
  tone = 'primary',
  onConfirm,
  onCancel,
  busy = false,
}: ConfirmDialogProps): JSX.Element {
  return (
    <Modal
      open={open}
      title={title}
      onClose={onCancel}
      actions={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button
            variant={tone === 'destructive' ? 'destructive' : 'primary'}
            onClick={() => void onConfirm()}
            disabled={busy}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {body ? <div>{body}</div> : null}
    </Modal>
  );
}
